import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Summarize as SummarizeIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { TWButton } from '../../../shared/components';
import { colors, typography } from '../../../shared/styles/theme';
import { postGenerateSummary, patchSummary } from '../utils/contractApi';
import type { ContractSession } from '../types/contracts';

/** Renders **bold** markdown spans. */
function renderBold(raw: string) {
  const parts = raw.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <Box component="strong" key={i} sx={{ fontWeight: typography.fontWeight.bold }}>
        {part.slice(2, -2)}
      </Box>
    ) : (
      part
    ),
  );
}

/** Parses a markdown summary string into intro text + headed sections. */
function parseSummary(text: string) {
  let normalized = text.trim();
  const redundantHeading = /^##\s+Executive Summary[^\n]*\n?/i;
  if (redundantHeading.test(normalized)) normalized = normalized.replace(redundantHeading, '');
  const parts = normalized.split(/\n(?=##\s)/);
  let introText = '';
  const sectionList: Array<{ heading: string; content: string }> = [];
  parts.forEach((part) => {
    const trimmed = part.trim();
    if (/^##\s/.test(trimmed)) {
      const lines = trimmed.split('\n');
      const heading = lines[0].replace(/^##\s+/, '').trim();
      const content = lines.slice(1).join('\n').trim();
      sectionList.push({ heading, content });
    } else if (trimmed) {
      introText = trimmed;
    }
  });
  return { intro: introText, sections: sectionList };
}

/** Renders headed sections as a 2-column grid. */
function SummarySections({ sections }: { sections: Array<{ heading: string; content: string }> }) {
  if (sections.length === 0) return null;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
      {sections.map((section, i) => (
        <Box
          key={i}
          sx={{
            borderLeft: `3px solid ${colors.primaryLight}`,
            pl: 2, py: 1.5, pr: 1.5,
            backgroundColor: colors.background.page,
            borderRadius: '0 6px 6px 0',
          }}
        >
          <Typography sx={{ fontSize: '0.68rem', fontWeight: typography.fontWeight.bold, color: colors.text.secondary, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
            {section.heading}
          </Typography>
          <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.primary, lineHeight: 1.55 }}>
            {renderBold(section.content)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export interface ContractSummaryProps {
  session: ContractSession;
  sessionUuid: string;
  clauseCount: number;
  hasAssessmentRun?: boolean;
  isSigned?: boolean;
  sx?: Record<string, unknown>;
  onSummaryUpdated: (updated: Pick<ContractSession, 'ai_summary' | 'final_summary'>) => void;
}

const ContractSummaryComponent: React.FC<ContractSummaryProps> = ({
  session,
  sessionUuid,
  clauseCount,
  hasAssessmentRun = true,
  isSigned = false,
  sx,
  onSummaryUpdated,
}) => {
  const displaySummary = session.final_summary ?? session.ai_summary ?? null;
  const [generating, setGenerating] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Parse summary into always-visible intro + collapsible sections
  const { summaryIntro, summarySections } = useMemo(() => {
    if (!displaySummary) return { summaryIntro: '', summarySections: [] as Array<{ heading: string; content: string }> };
    const { intro, sections } = parseSummary(displaySummary);
    return { summaryIntro: intro, summarySections: sections };
  }, [displaySummary]);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success');
  const [error, setError] = useState<string | null>(null);
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const showSnack = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackMessage(message);
    setSnackSeverity(severity);
  }, []);

  const handleGenerate = useCallback(async () => {
    setConfirmGenerateOpen(false);
    setGenerating(true);
    setError(null);
    try {
      const res = await postGenerateSummary(sessionUuid);
      onSummaryUpdated({ ai_summary: res.ai_summary, final_summary: res.final_summary });
      showSnack('Summary generated.', 'success');
    } catch (e) {
      const msg = e && typeof e === 'object' && 'response' in e && e.response && typeof (e.response as { data?: { detail?: string } }).data?.detail === 'string'
        ? (e.response as { data: { detail: string } }).data.detail
        : 'Failed to generate summary.';
      setError(msg);
      showSnack(msg, 'error');
    } finally {
      setGenerating(false);
    }
  }, [sessionUuid, onSummaryUpdated, showSnack]);

  const onGenerateClick = useCallback(() => {
    setConfirmGenerateOpen(true);
  }, []);

  const startEdit = useCallback(() => {
    setEditText(displaySummary ?? '');
    setEditing(true);
    setError(null);
  }, [displaySummary]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setEditText('');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      const res = await patchSummary(sessionUuid, { final_summary: trimmed });
      onSummaryUpdated({ ai_summary: res.ai_summary, final_summary: res.final_summary });
      setEditing(false);
      setEditText('');
      showSnack('Summary updated.', 'success');
    } catch (e) {
      const msg = e && typeof e === 'object' && 'response' in e && e.response && typeof (e.response as { data?: { detail?: string } }).data?.detail === 'string'
        ? (e.response as { data: { detail: string } }).data.detail
        : 'Failed to update summary.';
      setError(msg);
      showSnack(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [sessionUuid, editText, onSummaryUpdated, showSnack]);

  const handleCopy = useCallback(async () => {
    const raw = session.final_summary ?? session.ai_summary ?? '';
    if (!raw) {
      showSnack('No summary to copy.', 'error');
      return;
    }
    // Strip markdown so the copied text is plain and clean:
    // - "## Heading" → "Heading" (with a blank line before it for spacing)
    // - "**bold**" → "bold"
    const plain = raw
      .replace(/^##\s+(.+)$/gm, '\n$1')   // ## Heading → blank line + heading text
      .replace(/\*\*([^*]+)\*\*/g, '$1')   // **bold** → bold
      .replace(/^\n/, '')                   // remove leading blank line if present
      .trim();
    try {
      await navigator.clipboard.writeText(plain);
      showSnack('Copied to clipboard.', 'success');
    } catch {
      showSnack('Copy failed. You can select and copy manually.', 'error');
    }
  }, [session.final_summary, session.ai_summary, showSnack]);

  if (isSigned) return null;

  const hasClauses = clauseCount > 0;
  const generateDisabled = !hasClauses || !hasAssessmentRun;

  const generateButton = (
    <span style={{ flexShrink: 0 }}>
      <TWButton
        label={generating ? 'Generating…' : displaySummary ? 'Regenerate with AI' : 'Generate with AI'}
        onClick={onGenerateClick}
        loading={generating}
        disabled={generating || generateDisabled}
        startIcon={!generating ? <AutoAwesomeIcon /> : undefined}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap', py: 0.75, fontSize: typography.fontSize.sm }}
      />
    </span>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        backgroundColor: colors.background.card,
        border: `1px solid ${colors.border.light}`,
        borderRadius: 1.5,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            size="small"
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            aria-label={summaryExpanded ? 'Collapse summary' : 'Expand summary'}
            sx={{ color: colors.text.secondary }}
          >
            {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <SummarizeIcon sx={{ color: colors.primary, fontSize: '1.25rem' }} />
          <Typography
            variant="h4"
            component="span"
            sx={{
              fontFamily: typography.fontFamily.heading,
              fontWeight: typography.fontWeight.bold,
              color: colors.text.primary,
              fontSize: '1.5rem',
            }}
          >
            Executive Summary
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', flexShrink: 0, alignItems: 'center' }}>
          {editing ? (
            <>
              <TWButton label={saving ? 'Saving…' : 'Save'} onClick={handleSaveEdit} loading={saving} disabled={saving || !editText.trim()} />
              <Button variant="text" onClick={cancelEdit} disabled={saving} sx={{ textTransform: 'none', color: colors.text.secondary }}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {isSigned ? (
                <Tooltip title="Only available for unsigned contracts">
                  {generateButton}
                </Tooltip>
              ) : generateDisabled && hasClauses ? (
                <Tooltip title="Run AI Assessment first">
                  {generateButton}
                </Tooltip>
              ) : (
                generateButton
              )}
            </>
          )}
        </Box>
      </Box>

      {/* ── Error ── */}
      {error && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* ── Always-visible intro preview ── */}
      {displaySummary && !editing && summaryIntro && (
        <Box sx={{ px: 3, pb: summaryExpanded ? 0 : 2 }}>
          <Typography sx={{ fontSize: typography.fontSize.base, lineHeight: 1.7, color: colors.text.primary }}>
            {renderBold(summaryIntro)}
          </Typography>
        </Box>
      )}

      <Collapse in={summaryExpanded}>
      <Box sx={{ px: 3, pb: displaySummary && !editing ? 0 : 3 }}>
        {editing ? (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={16}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              placeholder="Enter or edit the executive summary…"
              variant="outlined"
              disabled={saving}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: typography.fontSize.base,
                  fontFamily: typography.fontFamily.primary,
                },
              }}
            />
          </Box>
        ) : displaySummary ? (
          <SummarySections sections={summarySections} />
        ) : (
          <Typography variant="body1" sx={{ color: colors.text.secondary }}>
            No summary yet. Generate a summary from your clause assessments after running AI assessment.
          </Typography>
        )}
      </Box>

      {/* ── Footer ── */}
      {displaySummary && !editing && (
        <>
          <Divider sx={{ mt: 2 }} />
          <Box
            sx={{
              px: 3,
              py: 1.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Button
                variant="text"
                size="small"
                startIcon={<CopyIcon sx={{ fontSize: '1rem' }} />}
                onClick={handleCopy}
                sx={{ textTransform: 'none', color: colors.text.secondary, fontSize: typography.fontSize.sm, '&:hover': { color: colors.primary, backgroundColor: 'transparent' } }}
              >
                Copy to Clipboard
              </Button>
            </Box>
            <TWButton
              label="Edit Summary"
              onClick={startEdit}
              disabled={generating}
              startIcon={<EditIcon />}
            />
          </Box>
        </>
      )}

      </Collapse>

      {/* ── Confirm generate dialog ── */}
      <Dialog open={confirmGenerateOpen} onClose={() => setConfirmGenerateOpen(false)}>
        <DialogTitle>Generate summary</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {displaySummary
              ? 'Summary will be regenerated for all NON_NEGOTIABLE clauses. You may edit responses before proceeding. Do you want to continue?'
              : 'Generate an AI summary for all NON_NEGOTIABLE clauses? This will be used to reply to the contract security review requester. This may take a moment.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmGenerateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerate} disabled={generating} color="primary">
            {generating ? 'Generating…' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackMessage}
        autoHideDuration={4000}
        onClose={() => setSnackMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackSeverity} onClose={() => setSnackMessage(null)}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ContractSummaryComponent;
